import type { FeedbackAccess } from "../feedback/send.ts";

/**
 * What the game menu shows and how it moves (P1.9-T14): pure data moved by events, so every move is decided
 * here and unit-tested without a page, and the view draws a state while the page runs what an item asks for.
 *
 * The menu opens on a request (Escape with no layer open, or the menu button) only once the world is
 * revealed: the loading shell holds the keyboard until then, but the input router still reports Escape as a
 * request for the menu, and a request made behind the shell is not remembered. Resume and a dismissal
 * (Escape or Tab on the menu) close it. Settings opens over the menu and backs out to it; whether it may back
 * out while changes are pending is the Settings window's to ask (ADR-061's amendment "Settings in sections, and
 * nothing applies until Apply"), and it reports `backed` only once it leaves. Log out, an item of
 * the menu, and Log out everywhere, a button in Settings' Account section, are an account's alone, since a `?token=`
 * dev session has no session to end; while the tab is leaving by one of them the menu and Settings stay, what
 * it leaves by busy and every other item disabled, and refuse every other move, as a busy confirm dialog does,
 * until a confirm the player cancels lets them stay. Change character (P1.9-T62), also an account's alone, asks
 * first in the same way; once confirmed the page departs the world, and concealing then closes the menu and ends
 * the leaving, since the page comes back to the world with another character rather than reloading. There is no
 * rename item (ADR-064).
 *
 * When the page leaves the world it showed (P1.9-T15: a close, or a sign-in that ended), the menu closes
 * with Settings, since it stacks over the loading shell's notice, and opens nothing until the world is shown
 * again. A menu busy with the tab's own leaving stays until the page reloads; a confirm the player cancels
 * then closes it.
 *
 * Feedback (ADR-081's amendment of 2026-10-05) is an item of a page that has somewhere to send a report. Its form
 * opens over the menu and backs out to it, as Settings does. The item is disabled while the account's feedback is
 * paused or turned off, which the gateway tells the page at its join, when a GM changes it, or in a refused send;
 * the view shows the sentence beside the menu. The gateway's offer widens what the form may send from a bug report
 * to both kinds.
 *
 * Change look (ADR-094's amendment of 2026-10-06, B11) is an item of an account's page that offers the temporary
 * screen. Its window opens over the menu and backs out to it, as Settings does; an accepted change closes it to the
 * world. The item is disabled while the page's freeze notice stands, and the view says why beside the menu.
 */

/**
 * Which surface of the menu shows: none, the menu, or Settings, the Feedback form or the Change look window over
 * the menu.
 */
export type GameMenuView = "closed" | "menu" | "settings" | "feedback" | "look";

/**
 * What the player can choose: an item of the menu (Resume, Settings, Inventory, Change look, Feedback, Change character, Log out), or, in
 * Settings, Log out everywhere, in its Account section. Inventory (P2.8-T11) is the Inventory window's pointer and
 * touch path: it closes the menu, and the page opens the window behind it. Emotes (P2.19-T8, ADR-114 decision 1.5) is
 * the emote wheel's, in the same way.
 */
export type GameMenuItem = "resume" | "settings" | "inventory" | "character" | "emotes" | "look" | "feedback" | "change" | "logout" | "everywhere";

/**
 * What the menu shows of the emote wheel: `none` until the gateway has offered emotes, when the item is not shown
 * (ADR-114 decision 6.2); `off` while the wheel cannot open, the character frozen or dead, when the item is shown
 * disabled; `on` when choosing it opens the wheel.
 */
export type EmotesOffer = "none" | "off" | "on";

/**
 * What the page's Feedback sends and whether it may: a bug report alone, or both kinds once the gateway offered
 * them (a development page sending to the dev server holds both from the start); and the account's access.
 */
export interface FeedbackEntry {
  readonly kinds: "bug" | "all";
  readonly access: FeedbackAccess;
}

/**
 * How the tab is leaving: changing character (asking first, then returning to character select), logging out,
 * or logging out everywhere (asking first, then ending every session).
 */
export type GameMenuLeaving = "change" | "logout" | "everywhere";

/**
 * The menu's state: whether the world is revealed, whether the page plays an account session, which surface
 * shows, and how the tab is leaving, if it is.
 */
export interface GameMenuState {
  readonly revealed: boolean;
  readonly account: boolean;
  readonly view: GameMenuView;
  readonly leaving: GameMenuLeaving | null;
  /** The page's Feedback, or null on a page with nowhere to send a report. */
  readonly feedback: FeedbackEntry | null;
  /** Whether the page offers the temporary Change look screen. */
  readonly look: boolean;
  /** Whether the page's freeze notice stands. */
  readonly frozen: boolean;
  /** What the menu shows of the emote wheel. */
  readonly emotes: EmotesOffer;
  /**
   * The time on the page's clock at which the fight that locks Change character and Log out ends, as the gateway
   * last answered (ADR-084's amendment of 2026-10-13), or null while nothing locks them.
   */
  readonly lock: number | null;
}

/**
 * What moves the menu:
 *
 * - `revealed`: the world is shown and the loading shell has left the page.
 * - `requested`: Escape with no layer open, or the menu button.
 * - `chosen`: the player chose an item.
 * - `dismissed`: Escape or Tab on the menu, which asks it to close.
 * - `backed`: Settings leaves for the menu, by its close button, Cancel or Escape, once nothing is pending.
 * - `stayed`: the player cancelled changing character or logging out everywhere, or a fight refused a leave, so the
 *   tab stays.
 * - `concealed`: the page left the world, which the loading shell's notice now covers.
 * - `feedbackOffered`: the gateway offered the account both kinds of report.
 * - `feedbackAccess`: the account's feedback is open, paused or off, as the gateway or the page's clock says.
 * - `frozen`: the page's freeze notice came or went.
 * - `emotes`: what the menu shows of the emote wheel changed.
 * - `lock`: the gateway said how long a fight locks leaving, or the lock ran out.
 * - `lookChanged`: the gateway accepted a change of look, so the Change look window closes to the world.
 */
export type GameMenuEvent =
  | { readonly type: "revealed" }
  | { readonly type: "requested" }
  | { readonly type: "chosen"; readonly item: GameMenuItem }
  | { readonly type: "dismissed" }
  | { readonly type: "backed" }
  | { readonly type: "stayed" }
  | { readonly type: "concealed" }
  | { readonly type: "feedbackOffered" }
  | { readonly type: "feedbackAccess"; readonly access: FeedbackAccess }
  | { readonly type: "frozen"; readonly frozen: boolean }
  | { readonly type: "emotes"; readonly emotes: EmotesOffer }
  | { readonly type: "lock"; readonly until: number | null }
  | { readonly type: "lookChanged" };

/**
 * One item as the menu shows it: busy while the tab leaves by it, disabled while it leaves by another.
 */
export interface GameMenuItemView {
  readonly item: GameMenuItem;
  readonly busy: boolean;
  readonly disabled: boolean;
}

/**
 * The items every session's menu holds, and the ones only an account's holds, in the order they show.
 */
const EVERY_SESSION: readonly GameMenuItem[] = ["resume", "settings", "inventory", "character"];
const EMOTES: readonly GameMenuItem[] = ["emotes"];
const FEEDBACK: readonly GameMenuItem[] = ["feedback"];
const LOOK: readonly GameMenuItem[] = ["look"];
const ACCOUNT_ONLY: readonly GameMenuItem[] = ["change", "logout"];

/**
 * The menu before the world is revealed: closed, with nothing leaving.
 *
 * @param account Whether the page plays an account session rather than a `?token=` dev session.
 * @param feedback The page's Feedback as it starts, or null when the page has nowhere to send a report.
 * @param look Whether the page offers the temporary Change look screen, which only an account's page shows.
 * @returns The state.
 */
export function initialGameMenuState(account: boolean, feedback: FeedbackEntry | null = null, look = false): GameMenuState {
  return { revealed: false, account, view: "closed", leaving: null, feedback, look: look && account, frozen: false, emotes: "none", lock: null };
}

/**
 * Whether two accesses are the same.
 *
 * @param a One.
 * @param b The other.
 * @returns True for the same state and, when paused, the same end.
 */
function sameAccess(a: FeedbackAccess, b: FeedbackAccess): boolean {
  return a.state === b.state && (a.state !== "paused" || b.state !== "paused" || a.until === b.until);
}

/**
 * The state after an item is chosen on the open menu, or Log out everywhere in Settings.
 *
 * @param state The state, with the menu open and nothing leaving.
 * @param item The item.
 * @returns The next state, or the same one when the item does nothing here.
 */
function choose(state: GameMenuState, item: GameMenuItem): GameMenuState {
  switch (item) {
    case "resume":
      return { ...state, view: "closed" };
    case "inventory":
    case "character":
      return state.view === "menu" ? { ...state, view: "closed" } : state;
    case "emotes":
      return state.view === "menu" && state.emotes === "on" ? { ...state, view: "closed" } : state;
    case "settings":
      return state.view === "menu" ? { ...state, view: "settings" } : state;
    case "feedback":
      return state.view === "menu" && state.feedback?.access.state === "open" ? { ...state, view: "feedback" } : state;
    case "look":
      return state.view === "menu" && state.look && !state.frozen ? { ...state, view: "look" } : state;
    case "change":
    case "logout":
      return state.account && state.view === "menu" ? { ...state, leaving: item } : state;
    case "everywhere":
      return state.account && state.view === "settings" ? { ...state, leaving: item } : state;
  }
}

/**
 * Moves the menu by one event.
 *
 * @param state The state.
 * @param event What happened.
 * @returns The next state, or the same object when the event changes nothing.
 * @example
 * gameMenuReducer(initialGameMenuState(true), { type: "requested" });
 * // the same state: behind the loading shell a request opens nothing
 */
export function gameMenuReducer(state: GameMenuState, event: GameMenuEvent): GameMenuState {
  if (event.type === "revealed") {
    return state.revealed ? state : { ...state, revealed: true };
  }
  if (event.type === "stayed") {
    return state.leaving === null ? state : { ...state, leaving: null, view: state.revealed ? state.view : "closed" };
  }
  if (event.type === "concealed") {
    if (!state.revealed) {
      return state;
    }
    return state.leaving === null || state.leaving === "change" ? { ...state, revealed: false, view: "closed", leaving: null } : { ...state, revealed: false };
  }
  if (event.type === "feedbackOffered") {
    return state.feedback === null || state.feedback.kinds === "all" ? state : { ...state, feedback: { ...state.feedback, kinds: "all" } };
  }
  if (event.type === "feedbackAccess") {
    return state.feedback === null || sameAccess(state.feedback.access, event.access) ? state : { ...state, feedback: { ...state.feedback, access: event.access } };
  }
  if (event.type === "frozen") {
    return state.frozen === event.frozen ? state : { ...state, frozen: event.frozen };
  }
  if (event.type === "emotes") {
    return state.emotes === event.emotes ? state : { ...state, emotes: event.emotes };
  }
  if (event.type === "lock") {
    return state.lock === event.until ? state : { ...state, lock: event.until };
  }
  if (state.leaving !== null) {
    return state;
  }
  switch (event.type) {
    case "requested":
      return state.revealed && state.view === "closed" ? { ...state, view: "menu" } : state;
    case "chosen":
      return state.view === "closed" ? state : choose(state, event.item);
    case "dismissed":
      return state.view === "closed" ? state : { ...state, view: "closed" };
    case "backed":
      return state.view === "settings" || state.view === "feedback" || state.view === "look" ? { ...state, view: "menu" } : state;
    case "lookChanged":
      return state.view === "look" ? { ...state, view: "closed" } : state;
  }
}

/**
 * The items the menu shows, in order: Resume, Settings and Inventory, Change look on an account's page that offers
 * the temporary screen, Feedback on a page that has somewhere to send a report, then Change character and Log out
 * for an account. While the tab leaves, the item it leaves by is busy and every other is disabled; while it leaves
 * everywhere, from Settings, every item is disabled. Feedback is disabled while the account's feedback is paused or
 * off, Change look while the page's freeze notice stands, and Change character and Log out while a fight locks
 * them.
 *
 * @param state The state.
 * @returns The items, or none while the menu is closed.
 */
export function gameMenuItems(state: GameMenuState): readonly GameMenuItemView[] {
  if (state.view === "closed") {
    return [];
  }
  const items = [...EVERY_SESSION, ...(state.emotes === "none" ? [] : EMOTES), ...(state.look ? LOOK : []), ...(state.feedback === null ? [] : FEEDBACK), ...(state.account ? ACCOUNT_ONLY : [])];
  const closed = state.feedback !== null && state.feedback.access.state !== "open";
  return items.map((item) => ({ item, busy: state.leaving === item, disabled: (state.leaving !== null && state.leaving !== item) || (item === "feedback" && closed) || (item === "look" && state.frozen) || ((item === "change" || item === "logout") && state.lock !== null) || (item === "emotes" && state.emotes === "off") }));
}

/**
 * How Settings' Log out everywhere shows: absent for a `?token=` dev session, busy while the tab leaves by it,
 * disabled while it leaves by Log out or Change character, and otherwise ready.
 *
 * @param state The state.
 * @returns The button's state, or null when Settings has no Account section.
 */
export function everywhereButton(state: GameMenuState): { readonly busy: boolean; readonly disabled: boolean } | null {
  if (!state.account) {
    return null;
  }
  return { busy: state.leaving === "everywhere", disabled: state.leaving === "logout" || state.leaving === "change" };
}
